import type { Klass, LexicalNode, EditorThemeClasses } from 'lexical';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { CodeNode, CodeHighlightNode } from '@lexical/code';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { TableNode, TableRowNode, TableCellNode } from '@lexical/table';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';

/** The editor's serialized Lexical document (SerializedEditorState JSON). */
export type { ScriptDoc } from '@discovr/types';

export const SCRIPT_DOC_NAMESPACE = 'script-doc';

/** Upper bound on the serialized doc-JSON, mirroring the server's
 *  `maxDocBytes` (internal/campaigns/script_doc_validate.go) so the client can
 *  refuse an oversized script with a clear message before the round-trip.
 *  Sized for a 1 lakh-character script with heavy formatting. */
export const MAX_SCRIPT_DOC_BYTES = 4_000_000;

/** Script length cap in characters (1 lakh), mirroring the server's
 *  `limits.MaxScriptChars`. Counted as UTF-16 code units (String.length) on the
 *  plain text, matching the editor counter and the server check. */
export const MAX_SCRIPT_CHARS = 100_000;

/** There is no dedicated title field or node type — a script's "title" is
 *  simply its first block, styled prominently (Notion/Medium-style). Applied
 *  positionally via a first-child selector on the editable host, so it works
 *  retroactively on already-stored docs too, with no data-shape change.
 *
 *  Excludes ul/ol/table: font-size and font-weight are CSS-inherited
 *  properties, so an unscoped :first-child selector would cascade the
 *  oversized bold styling into every list item / table cell, not just style
 *  one "title" line, if a script happens to open with a list or table.
 *
 *  `SCRIPT_TITLE_CLASSES` is what the scaffold placeholder's title line uses
 *  directly; `SCRIPT_TITLE_FIRST_CHILD_CLASSES` is the same styling expressed
 *  as Tailwind arbitrary-variant utilities for the editor/renderer's host
 *  element (Tailwind's class scanner needs each utility spelled out — these
 *  two must be kept in sync by hand). */
export const SCRIPT_TITLE_CLASSES = 'text-2xl/tight font-bold';
export const SCRIPT_TITLE_FIRST_CHILD_CLASSES =
  '[&>:first-child:not(ul):not(ol):not(table)]:text-2xl/tight [&>:first-child:not(ul):not(ol):not(table)]:font-bold';

/** Toolbar text colours, stored as inline TextNode styles so they round-trip
 *  natively through the doc-JSON. */
export const SCRIPT_TEXT_COLORS = [
  { name: 'red', hex: '#dc2626' },
  { name: 'orange', hex: '#ea580c' },
  { name: 'green', hex: '#16a34a' },
  { name: 'blue', hex: '#2563eb' },
  { name: 'purple', hex: '#9333ea' },
  { name: 'gray', hex: '#6b7280' },
] as const;

/** Node set shared by the editor and renderer — both must register the same
 *  list. Paragraph and text are built in. */
export const SCRIPT_DOC_NODES: ReadonlyArray<Klass<LexicalNode>> = [
  HeadingNode,
  QuoteNode,
  ListNode,
  ListItemNode,
  CodeNode,
  CodeHighlightNode,
  LinkNode,
  AutoLinkNode,
  TableNode,
  TableRowNode,
  TableCellNode,
  HorizontalRuleNode,
];

/** Tailwind classes per node/format type (no `prose` in this package). Shared by
 *  the editor and renderer so both render identically. */
export const scriptDocTheme: EditorThemeClasses = {
  paragraph: 'my-1',
  heading: {
    h1: 'mt-4 mb-2 text-2xl font-bold',
    h2: 'mt-3 mb-2 text-xl font-bold',
    h3: 'mt-2 mb-1 text-lg font-bold',
    h4: 'mt-2 mb-1 text-base font-bold',
    h5: 'mt-2 mb-1 text-sm font-bold',
    h6: 'mt-2 mb-1 text-sm font-semibold',
  },
  quote: 'my-2 border-l-4 border-border pl-4 italic text-muted-foreground',
  list: {
    ul: 'my-2 list-disc pl-6',
    ol: 'my-2 list-decimal pl-6',
    listitem: 'my-0.5',
    listitemChecked: 'my-0.5 line-through text-muted-foreground',
    listitemUnchecked: 'my-0.5',
    nested: { listitem: 'list-none' },
  },
  link: 'text-primary underline',
  code: 'my-2 block overflow-x-auto rounded bg-muted p-3 font-mono text-sm',
  table: 'my-2 w-full border-collapse',
  tableCell: 'border border-border px-2 py-1 align-top',
  tableCellHeader: 'border border-border bg-muted px-2 py-1 text-left font-semibold',
  text: {
    bold: 'font-bold',
    italic: 'italic',
    underline: 'underline',
    strikethrough: 'line-through',
    underlineStrikethrough: 'underline line-through',
    subscript: 'align-sub text-xs',
    superscript: 'align-super text-xs',
    code: 'rounded bg-muted px-1 py-0.5 font-mono text-sm',
    highlight: 'rounded bg-yellow-200 px-0.5 dark:bg-yellow-500/40',
  },
};
